/** The public route that lets a buyer understand the work before deciding on a call. */
export const OWNLY_PROJECT_HREF = "/before-we-talk";

/**
 * WHAT OWNLY SELLS, IN ONE PLACE. Import it. Do not retype it.
 *
 * Written 2026-08-31, after Tom read his own home page and said "dont get what we're
 * meant to be offering here". An audit found the site describing the offer SIXTEEN
 * different ways across six pages, in five taxonomies that did not map onto each other:
 * the home page verbs, the /services outcomes, the ServicesPageJsonLd services, the
 * /for-business-owners pillars and the six packages. `/` and `/services` had the same H1
 * word for word. No page taught the vocabulary of the next page, so no page was the
 * answer and Google ranked none of them: 22 clicks in 90 days, 21 of them the brand name.
 *
 * The line Tom chose: the systems the business runs on. Spreadsheets, quoting, chasing,
 * the file only one person understands. Websites are the way in, not the headline.
 *
 * A seventeenth phrasing is one careless afternoon away, so the words live here and
 * scripts/check-definition-of-done.mjs fails the build on a CTA that invents its own.
 */
export const OWNLY_OFFER = {
  /**
   * The headline. Names the deliverable in the customer's own words.
   *
   * Plural since 2026-09-01. Tom: "systems your business runs on instead of system". A business
   * does not run on one system, it runs on the handful that do not talk to each other, and the
   * singular quietly promised one thing where the offer is the set.
   *
   * Kept short on purpose. The first draft was "We build the system your business
   * is running on spreadsheets", which is a better sentence and a worse headline: at 4.5rem in
   * a seven-of-twelve column it ran to four lines and left "spreadsheets." alone on the last
   * one. The spreadsheet is the hook, so it moved down into the sub-line where the concrete
   * detail belongs, and the headline kept the promise.
   */
  headline: "We build the systems your business runs on.",
  /** Split in two so a line can only break between them, never inside either half. */
  headlineLead: "We build the systems",
  headlineAccent: "your business runs on.",
  /**
   * Under the headline. Concrete nouns, and NO PRICE.
   *
   * It used to end "Fixed price from £2,500 to build, then a monthly fee to run it." Tom,
   * 2026-09-01: "they literally enter the site and the first thing we show them is the pricing
   * with no value prior, no past work or anything." He is right about the order. A number quoted
   * before anyone has seen what we have built is asking to be compared on cost alone, and it was
   * the second sentence on the page.
   */
  subline:
    "Right now it is a spreadsheet, a shared inbox, and the file only one person understands. We replace it with something built for how the business actually works.",
  /** The category, for meta descriptions and anywhere a plain noun phrase is needed. */
  /* No geography. See the identity note in the lead engine: "not just for UK businesses, we need
     to stop saying that and shutting out others." Preston stays in the meta descriptions, which is
     a location we work FROM rather than a limit on who we work for. */
  category: "Custom software, apps and websites",
  /** The two verbs. Every call to action on the site is one of these, and only these. */
  /* Was "Get a price", which made the first thing asked of a visitor a question about money.
     The destination is unchanged: it has always been the project form. */
  primaryCta: "Watch the briefing",
  /* "Book a 30-minute review", Tom's decision 2026-09-23 (was "Book a call"). Same words as
     BOOKING_CTA in lib/booking.ts; typed out here because this object is `as const`. */
  secondaryCta: "Book a 30-minute review",
} as const;

/**
 * Where the internal staff system lives. The marketing site never handles
 * credentials: the footer link is a handoff, and the app owns sign-in.
 *
 * It answers on `app.ownlydigital.online` as well, and that host must keep
 * working: every link in an email we have already sent points at it. New links
 * use this one because it reads as ours to a client who only knows the .co.uk
 * brand, and because it is the domain people can remember without being told.
 *
 * NOT for anything embedded in cold outreach. Those links stay on the .online
 * host so that spam complaints cannot reach the main domain. See
 * `outreachEmailOrigin` in the lead engine.
 */
export const OWNLY_APP_URL = (
  process.env.NEXT_PUBLIC_OWNLY_APP_URL?.trim() || "https://app.ownlydigital.co.uk"
).replace(/\/$/, "");

/**
 * Registered company identity. The Companies Act 2006 (and the e-commerce
 * regulations) require a UK limited company to publish its registered name,
 * number, place of registration and registered office on its website.
 *
 * These are also the values every KYC / business-verification check is matched
 * against, so they must stay character-for-character identical to the Companies
 * House record. Do not "tidy" them into the trading style ("Ownly Digital") or
 * the marketing location ("Preston") — a mismatch fails verification.
 *
 * Source of truth:
 * https://find-and-update.company-information.service.gov.uk/company/17266711
 */
export const OWNLY_LEGAL = {
  /** Exact registered name. Not the trading style. */
  name: "Ownly Digital Limited",
  number: "17266711",
  placeOfRegistration: "England and Wales",
  registeredOffice: {
    street: "Unit 6 Balfour Court",
    locality: "Leyland",
    region: "Lancashire",
    postalCode: "PR25 2TF",
    country: "GB",
  },
  /**
   * Registration with the Information Commissioner's Office, the UK data protection regulator.
   * Shown as plain text with a link to the public register entry, never as a logo or badge: the
   * ICO does not allow its logo to be used to suggest it endorses a business, and a number anyone
   * can look up is the stronger signal anyway. Must match the register entry exactly.
   */
  ico: {
    number: "ZC168993",
    registerUrl: "https://ico.org.uk/ESDWebPages/Entry/ZC168993",
  },
  /**
   * Business insurance (Tom, 2026-10-08: "We are fully insured, so you can put it on the
   * website"). From the Hiscox schedule: Hiscox Insurance Company Limited, arranged through
   * PolicyBee, 8 Oct 2026 to 7 Oct 2027, continuing cover. NEVER publish the policy number or
   * the premium. Renew these figures when the policy renews in October 2027.
   */
  insurance: {
    insurer: "Hiscox",
    professionalIndemnity: "£1m",
    publicLiability: "£1m",
    cyber: "£100,000",
  },
  /** The public Companies House record, for links that let a reader check the number. */
  companiesHouseUrl:
    "https://find-and-update.company-information.service.gov.uk/company/17266711",
  /** Incorporation date from the register (ISO). Used as `foundingDate` in structured data only. */
  incorporated: "2026-06-08",
  /** SIC code on the register: 62012, business and domestic software development. */
  sic: "62012",
} as const;

/**
 * The Google Business Profile, by its Maps CID. NOT A GUESS: derived from the profile's own
 * "Ask for reviews" link that Tom supplied on 2026-09-18 (g.page/r/CTUtZWbZplywECE), which
 * resolves to place 0x4f73dc1d4dc5d6a5:0xb05ca6d966652d35; 0xb05ca6d966652d35 is this CID.
 * Opened 2026-10-25: "Ownly Digital, Software company", ownlydigital.co.uk, +44 20 4587 1648.
 */
export const OWNLY_GOOGLE_BUSINESS_PROFILE_URL =
  "https://www.google.com/maps?cid=12708215701188717877";

/** Registered office as a single line, for footers and legal copy. */
export const OWNLY_REGISTERED_OFFICE_LINE = [
  OWNLY_LEGAL.registeredOffice.street,
  OWNLY_LEGAL.registeredOffice.locality,
  OWNLY_LEGAL.registeredOffice.postalCode,
].join(", ");

/** Canonical HTTPS origin for production (www is the preferred public host). */
const OWNLY_PRODUCTION_ORIGIN = "https://www.ownlydigital.co.uk";

/**
 * If `NEXT_PUBLIC_SITE_URL` points at the live Ownly domain (with or without www),
 * normalize to the single canonical origin so metadata, sitemap, and JSON-LD match
 * redirects and avoid apex/www duplicate signals.
 */
function normalizeExplicitSiteUrl(raw: string): string {
  const trimmed = raw.trim().replace(/\/$/, "");
  if (!trimmed) return trimmed;
  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    const u = new URL(withProtocol);
    const host = u.hostname.toLowerCase();
    if (host === "ownlydigital.co.uk" || host === "www.ownlydigital.co.uk") {
      return OWNLY_PRODUCTION_ORIGIN;
    }
  } catch {
    // keep trimmed string below
  }
  return trimmed;
}

/** Base URL for metadata (Open Graph, etc.). Set NEXT_PUBLIC_SITE_URL in production. */
export function getSiteUrl(): string {
  const explicit = process.env.NEXT_PUBLIC_SITE_URL?.trim();
  if (explicit) return normalizeExplicitSiteUrl(explicit);
  if (process.env.VERCEL_URL)
    return `https://${process.env.VERCEL_URL.replace(/^https?:\/\//, "")}`;
  return "http://localhost:3000";
}

/** Used when `NEXT_PUBLIC_CONTACT_EMAIL` is unset so mailto links always have a recipient. */
export const OWNLY_PUBLIC_CONTACT_EMAIL_DEFAULT = "team@ownlydigital.co.uk";

/**
 * Public phone number for the footer, tel: link, and Organization JSON-LD.
 * This is the Dialpad main line, not any individual's direct extension, so
 * calls route the same way regardless of who answers.
 */
export const OWNLY_PUBLIC_CONTACT_PHONE_DEFAULT = "+442045871648";
const OWNLY_PUBLIC_CONTACT_PHONE_DISPLAY_DEFAULT = "020 4587 1648";

/** E.164 form for `tel:` links and JSON-LD. Set `NEXT_PUBLIC_CONTACT_PHONE` to override. */
export function getPublicContactPhone(): string {
  return process.env.NEXT_PUBLIC_CONTACT_PHONE?.trim() || OWNLY_PUBLIC_CONTACT_PHONE_DEFAULT;
}

/** Human-readable form for on-page display. */
export function getPublicContactPhoneDisplay(): string {
  return (
    process.env.NEXT_PUBLIC_CONTACT_PHONE_DISPLAY?.trim() ||
    OWNLY_PUBLIC_CONTACT_PHONE_DISPLAY_DEFAULT
  );
}

/**
 * The opening line a visitor sees already typed when they tap "WhatsApp us", the same on every
 * page (Tom, 2026-10-11: one message everywhere, page-aware versions were considered and dropped).
 * It ends on a colon so they type what they need straight after it, and the first message we get
 * already says what they want rather than just "hi".
 */
export const OWNLY_WHATSAPP_PREFILL = "Hi Ownly, I'd like to chat about a system for my business. Here's a bit about what I need:";

/**
 * A wa.me link to the main line (Tom, 2026-10-11: WhatsApp and call buttons). It follows
 * `getPublicContactPhone` rather than holding its own copy of the number, so the WhatsApp button,
 * the tel: link and the structured data can never disagree. wa.me wants digits only, no plus.
 */
export function getWhatsAppHref(message: string = OWNLY_WHATSAPP_PREFILL): string {
  const digits = getPublicContactPhone().replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

/**
 * An OPTIONAL second line with a Lancashire dialling code. OFF BY DEFAULT, and that is the
 * decision, not an oversight.
 *
 * Tom asked for one on 2026-09-06, then asked the better question: "is there really any point".
 * There is not much. A local area code nudges conversion on local search traffic, but it is not
 * a ranking factor, and the local proof this site already carries is stronger and free: the
 * registered office genuinely is in Leyland, the Companies House number is in the footer of
 * every page, and one of the named clients is a practice in the same town. A Google Business
 * Profile at the Leyland address would do more for the local pack than a phone number ever will.
 *
 * Against it, the cost is not the line rental. The 020 is already printed on both app store
 * listings, every quote, every invoice and every email footer, and lead-engine's
 * `ownly-contact.ts` warns in as many words that "a client who reads one number on the website
 * and a different one on their invoice has to guess which is real". A second number is a second
 * thing to keep identical across five surfaces forever, and it splits call routing for a line
 * that will ring rarely while the estimator and the forms take the enquiries.
 *
 * So the code stays and the number does not. Set `NEXT_PUBLIC_LOCAL_PHONE` (E.164, e.g.
 * `+441772xxxxxx`) and `NEXT_PUBLIC_LOCAL_PHONE_DISPLAY` (e.g. `01772 xxx xxx`) and it appears
 * in the footer, on /book and in the Organization structured data. Leave them unset and nothing
 * anywhere renders a second number.
 *
 * THERE IS NO HARD-CODED FALLBACK ON PURPOSE. The first version of this shipped a placeholder
 * `01772 000000`, which is not a real subscriber line: Ofcom reserves no drama range for 01772,
 * and inventing a plausible one risks printing a stranger's number on a page that asks people to
 * ring it. A contact detail is either real or absent. It is never a stand-in.
 */
export function getPublicLocalPhone(): string | null {
  return process.env.NEXT_PUBLIC_LOCAL_PHONE?.trim() || null;
}

/**
 * Human-readable form of the optional local line, or null when there is not one.
 *
 * Falls back to the E.164 value rather than to nothing when only the dialable form is
 * configured: a number shown as `+441772...` is ugly, and a number silently missing because
 * half the configuration was set is worse.
 */
export function getPublicLocalPhoneDisplay(): string | null {
  const e164 = getPublicLocalPhone();
  if (!e164) return null;
  return process.env.NEXT_PUBLIC_LOCAL_PHONE_DISPLAY?.trim() || e164;
}

/**
 * Public contact for footer, privacy page, and project inquiry `mailto:`.
 * Set `NEXT_PUBLIC_CONTACT_EMAIL` to override (e.g. staging).
 * Values on the legacy `@growth-sync.com` domain are ignored so old deploy env
 * does not override the Ownly address.
 */
export function getPublicContactEmail(): string {
  const v = process.env.NEXT_PUBLIC_CONTACT_EMAIL?.trim();
  if (!v) return OWNLY_PUBLIC_CONTACT_EMAIL_DEFAULT;
  if (v.toLowerCase().endsWith("@growth-sync.com"))
    return OWNLY_PUBLIC_CONTACT_EMAIL_DEFAULT;
  return v;
}

/**
 * THE OFFICIAL PROFILES, IN CODE RATHER THAN IN AN ENV VAR.
 *
 * `sameAs` is how a search engine confirms that this site, that LinkedIn page and that
 * Instagram account are one organisation rather than three. Without it the site was telling
 * Google about the two apps and nothing else, so the company had no verified presence
 * attached to it at all. Tom, 2026-09-13, on Google's own "add social profiles" prompt.
 *
 * These live here, not only in `NEXT_PUBLIC_ORGANIZATION_SAME_AS`, because an env var that is
 * unset fails silently: the schema simply omits the field and nobody notices for months, which
 * is exactly what happened. The env var still overrides, for staging or for a profile we add
 * in a hurry, but the real profiles are the default.
 *
 * THE LINKEDIN SLUG IS `ownly-software` AND THAT IS NOT A TYPO. The page is displayed as
 * "Ownly Digital" but its URL kept the original handle. `linkedin.com/company/ownly-digital`
 * redirects to an unrelated digital marketing agency in India that also trades as Ownly
 * Digital, so pointing `sameAs` at the obvious-looking slug would hand our identity to them.
 * Verified against the live pages on 2026-09-13; check before changing.
 */
const OWNLY_SOCIAL_PROFILES = [
  "https://www.linkedin.com/company/ownly-software/",
  "https://www.instagram.com/ownly_software/",
  "https://www.facebook.com/people/Ownly-Digital/61592456668751/",
] as const;

/**
 * Public profile URLs for JSON-LD `sameAs` (e.g. LinkedIn, X). Comma-separated in
 * `NEXT_PUBLIC_ORGANIZATION_SAME_AS` to override; otherwise the profiles above.
 */
/*
 * ENTITY ANCHORS, ALWAYS INCLUDED (2026-10-25). Google's AI Overview for "Ownly Digital" merged
 * this company with Ownly Digital LLP, an unrelated agency in India (ownlydigital.com, since
 * 2017), and described us as its "UK operation". The register entry and the Google Business
 * Profile are the two records Google trusts most to say "this is a separate UK company", so they
 * are in sameAs whatever the env var says.
 *
 * NEVER ADD: facebook.com/ownlydigital ("Unleashing Business Opportunities", ~1.4K followers),
 * instagram.com/ownlydigital, linkedin.com/company/ownlydigital or /ownly-digital, ownlydigital.com
 * or clutch.co/profile/ownly-digital (no -0). All of those are the Indian company.
 */
const OWNLY_ENTITY_ANCHORS = [
  OWNLY_LEGAL.companiesHouseUrl,
  OWNLY_GOOGLE_BUSINESS_PROFILE_URL,
] as const;

export function getOrganizationSameAs(): string[] {
  const raw = process.env.NEXT_PUBLIC_ORGANIZATION_SAME_AS?.trim();
  if (!raw) return [...OWNLY_ENTITY_ANCHORS, ...OWNLY_SOCIAL_PROFILES];
  const fromEnv = raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => {
      try {
        return Boolean(s && new URL(s).protocol);
      } catch {
        return false;
      }
    });
  return [...new Set([...OWNLY_ENTITY_ANCHORS, ...fromEnv])];
}
